import { Injectable, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { EMPTY } from 'rxjs';
import { filter } from 'rxjs/operators';
import { OrigenNavegacionService } from './origen-navegacion.service';
import { esRutaSinPantalla, rutaDeUrl } from './rutas-sin-pantalla';

/**
 * La pantalla **de la que se vino** a la pantalla actual, para la flecha «volver» de las fichas que
 * no tienen una gestión madre de la que colgar (hoy, Almacén · Info producto).
 *
 * <h3>Qué recuerda</h3>
 * La URL completa —con su query— de la última pantalla cuya **ruta** era distinta de la actual.
 * Cambiar sólo la query dentro de la misma ruta no cuenta como cambiar de pantalla: Info producto
 * entra con `?q=<ean>` y se lo quita de la URL nada más atenderlo, y ese retoque no puede convertir a
 * Info producto en «la pantalla anterior» de sí misma.
 *
 * <h3>Por qué no `Location.back()`</h3>
 * Atrás del navegador no sabe si hay algo a lo que volver dentro de la aplicación —en una pestaña
 * recién abierta no hace nada— y no permite rotular la flecha. Con la URL en la mano, la flecha dice
 * a dónde va y, si no hay pantalla anterior, la ficha elige su respaldo (su hub).
 *
 * <h3>Vive en memoria</h3>
 * Tras recargar (F5) no hay pantalla anterior y la flecha va al respaldo. Es lo honesto: la pantalla
 * de la que se vino ya no está en esta sesión de navegación.
 *
 * Se instancia al arrancar (`app.config.ts`): tiene que estar escuchando desde la primera
 * navegación, no desde que la primera ficha lo inyecta.
 */
@Injectable({ providedIn: 'root' })
export class PantallaAnteriorService {
  private readonly router = inject(Router);
  private readonly origen = inject(OrigenNavegacionService);

  private readonly _anterior = signal<string | null>(null);
  /** URL actual con query; `null` hasta la primera navegación. */
  private urlActual: string | null = null;

  /** URL de la pantalla desde la que se llegó a la actual, o `null` si no la hay. */
  readonly anterior = this._anterior.asReadonly();

  constructor() {
    // `?? EMPTY`: los tests de componente montan routers falsos sin eventos; entonces no hay
    // pantalla anterior y la flecha va al respaldo.
    (this.router.events ?? EMPTY)
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => this.apuntar(e.urlAfterRedirects));
  }

  /**
   * Vuelve a la pantalla anterior o, si no la hay, a `respaldo`. Declara la navegación como
   * **chrome**: ir hacia atrás es lo contrario de salir de excursión por un enlace de la pantalla,
   * así que no abre árbol de navegación.
   */
  volver(respaldo: string): Promise<boolean> {
    return this.irA(this._anterior() ?? respaldo);
  }

  /**
   * Va a `destino` declarando la navegación como **chrome**. Para las fichas que deciden ellas el
   * destino de vuelta (porque congelan el que había al entrar) pero quieren el mismo trato que
   * {@link volver}: ir hacia atrás no abre árbol de navegación.
   */
  irA(destino: string): Promise<boolean> {
    this.origen.marcarChrome();
    return this.router.navigateByUrl(destino);
  }

  private apuntar(url: string): void {
    const rutaNueva = rutaDeUrl(url);
    const rutaActual = this.urlActual === null ? null : rutaDeUrl(this.urlActual);

    if (rutaActual !== null && rutaActual !== rutaNueva) {
      // El login, el logout y las pantallas de error no son un sitio al que volver.
      this._anterior.set(esRutaSinPantalla(rutaActual) ? null : this.urlActual);
    }
    this.urlActual = url;
  }
}
